import Feature from 'ol/Feature';
import * as olExtent from 'ol/extent';

export interface IRouteRecord {
    gpxGuid: string;
    displayName: string;
    notes?: string | null;
    colourHex: string;
    /**
     * The OpenLayers feature(s) for this route. Populated either from the
     * /features endpoint load on page open, or from the in-browser GPX parse
     * during upload. May be empty until the features response arrives.
     */
    features: Feature[];
    extent: olExtent.Extent | null;
    lengthMetres?: number;
    originalFileName?: string;
    isVisible: boolean;
}

/**
 * Centralised store for the user's GPX route records.
 *
 * Two upsert variants are provided to allow the high-frequency feature-stream
 * path (the /features endpoint) to avoid spamming onChange:
 *   - upsert      fires onChange (use for sidebar-driven or post-API updates)
 *   - upsertSilent skips onChange (use inside onFeatureAdded; let the caller
 *                  call scheduleUiRebuild instead)
 *
 * getMutable returns the live record reference so callers can mutate in-place
 * (e.g. extend an extent, push a feature) without a full upsert allocation.
 */
export class GpxRouteStore {

    private records: Map<string, IRouteRecord> = new Map();
    private onChangeCb: (() => void) | null = null;

    /** Set a single listener that fires whenever the store is mutated via upsert or remove. */
    public setOnChange(callback: () => void): void {
        this.onChangeCb = callback;
    }

    /** True once the sidebar API response has settled. Controls the loading spinner in the UI. */
    public routesLoaded: boolean = false;

    /** Insert or replace a record and notify the onChange listener. */
    public upsert(record: IRouteRecord): void {
        this.records.set(record.gpxGuid, record);
        this.onChangeCb?.();
    }

    /** Insert or replace a record without notifying. Use during feature streaming. */
    public upsertSilent(record: IRouteRecord): void {
        this.records.set(record.gpxGuid, record);
    }

    /** Return the live record reference so the caller can mutate fields in-place. */
    public getMutable(gpxGuid: string): IRouteRecord | undefined {
        return this.records.get(gpxGuid);
    }

    public get(gpxGuid: string): IRouteRecord | undefined {
        return this.records.get(gpxGuid);
    }

    public has(gpxGuid: string): boolean {
        return this.records.has(gpxGuid);
    }

    public getAll(): IRouteRecord[] {
        return Array.from(this.records.values());
    }

    /** Remove a record and notify the onChange listener. */
    public remove(gpxGuid: string): void {
        this.records.delete(gpxGuid);
        this.onChangeCb?.();
    }

    /**
     * Clear the OL feature arrays on every record so that onFeatureAdded
     * can repopulate them from a fresh /features response. Extents are preserved
     * as a fallback until the new features re-extend them.
     */
    public clearFeatures(): void {
        this.records.forEach(record => {
            record.features = [];
        });
    }
}
